import React from 'react';
import axios from "axios";
import AssetSwapDialog from "./AssetSwapDialog";
import { refreshAssetsFromServer } from "./FileManager";
import DeleteAssetDialog from './DeleteAssetDialog';
import APIUtility from "../../classes/APIUtility";
import AssetsManager from "../../classes/AssetsManager";
import MaterialAsset from "../../classes/MaterialAsset";
import { selectAllWithMaterial } from "../../classes/ShortcutHandler";
import Utils from "../../classes/Utilities";
import TimeUtils from "../../classes/utilities/TimeUtils";
import config from "../../config";
import { AssetTypes, MaterialTypes } from "../../globals";
import store, { StoreActions } from "../../state/store";
import ContextMenu from "../menus/ContextMenu";
import { AudioFileIcon, FolderIcon, HelpIcon, IconFillStyles, ImageFileIcon, MaterialAssetIcon, ModelFileIcon, UiTemplateIcon, VideoFileIcon } from "../Iconset";
import ModalManager from "../layout/ModalManager";
import RevisionDialog from "../revision/revisionEditor/revisionDialog";
import RevisionItemDialog from "../revision/revisionItemDialog";
import UITemplateTypes, { listTemplateTypes } from "../uisystem/templates/types";
import ScenarioHealthUtils from '../../classes/utilities/ScenarioHealthUtils';
import { IGrapesJSProjectData, IVisageTemplate } from '../../interfaces/VisageUI';
import { IAssetProps } from '../../interfaces/Assets';
import { assert } from '../../classes/utilities/ErrorHandling';
import MessagePrompter from '../prompts/MessagePrompter';
import { emptyGrapesProject, generateDefaultVisageTemplates } from '../visageUI/defaultTemplates/DefaultTemplates';
import { synchronizeTemplateToSteps } from '../visageUI/GrapesJSUtilities';
import DeleteUITemplateAction from '../../actions/visageUI/DeleteUITemplateAction';
import DeleteTemplate from '../../actions/ui/DeleteTemplate';
import ResetFactoryTemplatesDialog from '../visageUI/ResetFactoryTemplatesDialog';
import Dialog from '../layout/Dialog';
import { getUuidv4, JsonCopy } from '../../classes/utilities/Misc';
import { DecoratorMap } from '../../classes/DecoratorMap';
import { AssetSubTrees } from '../../interfaces/AssetLibrary';

export const AssetTypeToIconMap = {
	[AssetTypes.Material]: <MaterialAssetIcon />,
	[AssetTypes.Revision]: <ModelFileIcon />,
	[AssetTypes.Model]: <ModelFileIcon />,
	[AssetTypes.Folder]: <FolderIcon />,
	[AssetTypes.Image]: <ImageFileIcon />,
	[AssetTypes.Video]: <VideoFileIcon />,
	[AssetTypes.Audio]: <AudioFileIcon />,
	[AssetTypes.UITemplate]: <UiTemplateIcon />,
	[AssetTypes.ModelProcessed]: <ModelFileIcon />,
	[AssetTypes.RevisionProcessed]: <ModelFileIcon />,
	[AssetTypes.Document]: <UiTemplateIcon />,
};

const allowedDuplicateAssetTypes = new Set<AssetTypes>([
	AssetTypes.Material,
	AssetTypes.UITemplate,
	AssetTypes.VisageTemplate,
]);

export default class AssetsLibraryUtilities
{
	static createMaterial(copyMaterial: MaterialAsset | null = null, callbackFunction: (newMaterial: MaterialAsset, newMaterialProps: any) => void = () => { })
	{
		let newMaterial: MaterialAsset;
		if (copyMaterial)
		{
			newMaterial = new MaterialAsset({ sourceMaterial: copyMaterial });
		}
		else
		{
			newMaterial = new MaterialAsset({ type: MaterialTypes.Standard });
		}

		const newMaterialProps = {
			dndOverrides: {},
			folder: "Materials",
			label: newMaterial.name,
			nodeId: newMaterial.guid,
			parent: "Materials",
			tag: "Project",
			type: "material",
		}

		AssetsManager.setPropsForAsset(newMaterial.guid, newMaterialProps);
		AssetsLibraryUtilities.selectAssets([newMaterial.guid], { autoScroll: true, autoExpand: true });
		refreshAssetsFromServer();
		if (callbackFunction) callbackFunction(newMaterial, newMaterialProps);
	}

	static createSlide(copySlide = null, callbackFunction: any = null)
	{
		const uiEntryId = getUuidv4();
		if (!copySlide)
		{
			store.dispatch(StoreActions.ui.setActiveTemplateForStep({
				slide: uiEntryId,
				template: UITemplateTypes.CUSTOM
			}));
		}
		else
		{
			const tempSlide = { ...store.getState().uiModule.slideMaster[copySlide] }
			tempSlide.name = tempSlide.name + ' copy';
			store.dispatch(StoreActions.ui.setSlideFromCopy({ slide: uiEntryId, content: tempSlide }));
		}

		const newSlideProps = {
			dndOverrides: {},
			folder: "UI Templates",
			label: "New UI Template",
			nodeId: uiEntryId,
			parent: "UI Templates",
			tag: "Project",
			type: "slide",
		}

		AssetsManager.setPropsForAsset(uiEntryId, newSlideProps);
		AssetsLibraryUtilities.selectAssets([uiEntryId], { autoScroll: true, autoExpand: true });
		refreshAssetsFromServer();
		if (callbackFunction) callbackFunction(newSlideProps);
		//if (listViewRef?.current?.htmlElementRef?.focus) listViewRef.current.htmlElementRef.focus();
	}

	static createVisageTemplate(options?: { existingTemplateId?: string, templateContent?: IGrapesJSProjectData, temporary?: boolean, templateName?: string }): string
	{
		const newTemplateId = getUuidv4();
		const newTemplateProps = {
			dndOverrides: {},
			folder: "visageTemplates",
			label: options?.templateName ? options.templateName : "New UI Template",
			nodeId: newTemplateId,
			parent: "visageTemplates",
			tag: "Project",
			type: AssetTypes.VisageTemplate,
		};

		const templates = Object.values<IVisageTemplate>(store.getState().visageModule.uiTemplateData);

		if(!options)
		{
			options = {};
		}

		if(options.existingTemplateId)
		{
			const existingTemplate = store.getState().visageModule.uiTemplateData[options.existingTemplateId];
			assert(existingTemplate, `template with id ${options.existingTemplateId} was not found`);

			const newTemplate = JsonCopy(existingTemplate) as IVisageTemplate;
			newTemplate.id = newTemplateId;
			newTemplate.name = Utils.getUniqueElementName(templates, newTemplate.name);
			newTemplate.sourceTemplateId = options.existingTemplateId;
			newTemplate.isDefault = false;
			newTemplate.isTemporary = options.temporary ?? false;
			newTemplate.previousState = JsonCopy(newTemplate.content);

			store.dispatch(StoreActions.visage.setUITemplateData({ templateId: newTemplate.id, data: newTemplate }));
		}
		else
		{
			store.dispatch(StoreActions.visage.setUITemplateData({ 
				templateId: newTemplateId,
				data: { 
					name: Utils.getUniqueElementName(templates, newTemplateProps.label),
					id: newTemplateId,
					content: options.templateContent ? JsonCopy(options.templateContent) : emptyGrapesProject(), 
					sourceTemplateId: "",
					isDefault: false,
					isTemporary: options.temporary ?? false,
					previousState: options.templateContent ? JsonCopy(options.templateContent) : emptyGrapesProject(),
					defaultTemplateID: "",
				} 
			}));
		}

		AssetsManager.setPropsForAsset(newTemplateId, newTemplateProps);

		// only select non-temporary templates - temporary templates are not supposed to show in the asset library until finalized
		if(!options.temporary)
		{
			AssetsLibraryUtilities.selectAssets([newTemplateId], { autoScroll: true, autoExpand: true });
			refreshAssetsFromServer();
		}

		return newTemplateId;
	}

	static removeAssetOnServer(assetId: string, assetTag: string): void
	{
		const urlencoded = new URLSearchParams();
		urlencoded.append("asset", assetId);
		urlencoded.append("srcroot", assetTag);

		axios.post(config.SERVER_ADDRESS + config.REMOVE_FILE_PATH, urlencoded)
			.then((response) =>
			{
				if (response.status === 200)
				{
					refreshAssetsFromServer();
				}
			})
			.catch(function (error)
			{
				console.log(error);
			});
	}

	static createFolderOnServer(folderName: string)
	{
		const urlencoded = new URLSearchParams();
		urlencoded.append("name", folderName);

		axios.post(config.SERVER_ADDRESS + config.CREATE_DIRECTORY_PATH, urlencoded)
			.then((response) =>
			{
				if (response.status === 200)
				{
					refreshAssetsFromServer();
				}
			})
			.catch(function (error)
			{
				console.log(error);
			});
	}

	static generateContextMenu(props, e)
	{
		if (store.getState().uiModule.editingSlide) return;
		if (props.noContextMenu) return;

		ContextMenu.show(e, [
			...(props.protected ? [] : [{
				id: 'delete',
				renderObject: 'Delete',
				callback: (assetId) => 
				{
					if(store.getState().preferencesModule.editorPreferences.enableDialogDeleteAsset)
					{
						ModalManager.addModal({ allowClickOut: false, content: <DeleteAssetDialog assetIds={[assetId]} /> })
					}
					else
					{
						AssetsLibraryUtilities.deleteAsset(props);
					}
				},
				callbackParams: [props.nodeId],
			}]),
			...(props.nodeId !== "materials" ? [] : [{
				id: 'deleteUnassignedMaterials',
				renderObject: 'Delete Unassigned Materials',
				callback: () => AssetsManager.DeleteUnassignedMaterials(),
			},
			{
				id: 'mergeIdenticalMaterials',
				renderObject: 'Merge Identical Materials',
				callback: () => AssetsManager.MergeIdenticalMaterials(),
			}
			]),
			...(props.protected ? [] : [{
				id: 'rename',
				renderObject: 'Rename',
				callback: () =>
				{
					const sID = "AssetsComponentView" + "_ITEMREF_" + props.nodeId;
					const oRef = DecoratorMap.getMapping(sID).renameFunctions;
					oRef.activateRename();
				}
			}]),
			...(!allowedDuplicateAssetTypes.has(props.type) ? [] :
				props.type === AssetTypes.UITemplate && props.nodeId === UITemplateTypes.QUIZ_RESULTS ? [] : // WC20-2820: forbidden from duplicating quiz results template
					[{
						id: 'duplicate',
						renderObject: 'Duplicate',
						callback: (assetId) =>
						{
							switch(props.type)
							{
								case AssetTypes.Material:
									const oldMaterial = MaterialAsset.getMaterialAssetByGuid(assetId);
									AssetsLibraryUtilities.createMaterial(oldMaterial);
									break;
								case AssetTypes.UITemplate:
									AssetsLibraryUtilities.createSlide(assetId);
									break;
								case AssetTypes.VisageTemplate:
									AssetsLibraryUtilities.createVisageTemplate({ existingTemplateId: assetId });
									break;
								default:
									break;
							}
						},
						callbackParams: [props.nodeId],
					}]
			),
			...((props.type === AssetTypes.Folder || !AssetsManager.isAssetUsedInProject(props.nodeId)) ? [] : [{
				id: 'Replace project asset1',
				renderObject: 'Replace project asset',
				callback: (assetProps) =>
				{
					ModalManager.addModal({ showBackdrop: true, content: <AssetSwapDialog sourceAssetProps={assetProps} /> });
				},
				callbackParams: [props],
			}]),
			...((props.type !== AssetTypes.Material) ? [] : [{
				id: 'Select Objects Using Material',
				renderObject: 'Select Objects Using Material',
				callback: (materialAssetId: string) =>
				{
					selectAllWithMaterial(materialAssetId);
				},
				callbackParams: [props.nodeId],
			}]),
			...((props.type !== AssetTypes.ModelProcessed && props.type !== AssetTypes.RevisionProcessed) ? [] : [{
				id: 'Create a Revision',
				renderObject: 'Optimize asset',
				callback: (assetProps) =>
				{
					ModalManager.addModal({ showBackdrop: true, content: <RevisionDialog selectedAsset={assetProps} /> });
				},
				callbackParams: [props],
			}]),
			...((props.type !== AssetTypes.RevisionProcessed) ? [] : [{
				id: 'View Revision Info',
				renderObject: 'View Optimization Info',
				callback: (assetProps) =>
				{
					APIUtility.getJobForRevision(assetProps.nodeId)
						.then((job) =>
						{
							const revisionData = {
								name: assetProps.label,
								revision: job.revision_name,
								creator: job.user,
								date: TimeUtils.formatDate(job.job_queue_date),
								startDate: TimeUtils.formatDate(job.process_start_date),
								endDate: TimeUtils.formatDate(job.process_end_date),
								status: job.process_status,
								id: job._id,
								rootFile: job.root_file_name,
								error: job.error,
								operations: job.operations,
								defaultOperations: job.default_operations && job.default_operations.length > 0 ? job.default_operations : null,
								totalPartsBefore: job.total_parts_before,
								totalPartsAfter: job.total_parts_after,
								totalPolyBefore: job.total_polycount_before,
								totalPolyAfter: job.total_polycount_after,
							};

							ModalManager.addModal({ content: <RevisionItemDialog item={revisionData} /> });
						})
						.catch((error) =>
						{
							console.error(error);
						});
				},
				callbackParams: [props],
			}]),
			...((props.type !== AssetTypes.VisageTemplate || props.defaultTemplateID === "") ? [] : [{
				id: 'Reset to Default Visage Template',
				renderObject: 'Reset to Default',
				callback: (assetProps) =>
				{
					ModalManager.addModal({
						content: 
						<Dialog title={"Reset to Default"}
							showCloseButton
							actions={[
								{
									label: "Reset",
									onClick: () => 
									{
										const defaultTemplate = Object.values<IVisageTemplate>(generateDefaultVisageTemplates()).find(template => template.defaultTemplateID === assetProps.defaultTemplateID);
										assert(defaultTemplate, "missing default template for given default id: " + assetProps.defaultTemplateID);
										if(!defaultTemplate) return; // assert above should throw but we need this for ts

										// set the content and previous state of the asset in the store to the default template
										store.dispatch(StoreActions.visage.setUITemplateData({ templateId: assetProps.nodeId, data: JsonCopy(defaultTemplate) }));
										synchronizeTemplateToSteps(assetProps.nodeId);
									}
								},
								{
									label: "Cancel",
								}
							]}
						>
							<p>
								This will restore the template to its default settings.
								<br/>
								This action cannot be undone.
								<br/>
								<br/>
								Reset template?
							</p>
						</Dialog>
					});
				},
				callbackParams: [props],
			}]),
			...(props.nodeId !== "visageTemplates" ? [] : [{
				id: 'Reset Factory Templates',
				renderObject: 'Reset to Default',
				callback: () =>
				{
					ModalManager.addModal({ content: <ResetFactoryTemplatesDialog /> });
				},
			}]),
		], "Assets");
	}

	static renameAssetLocally(assetProps: IAssetProps, newName: string)
	{
		const trimmedName = newName?.trim();
		if (!trimmedName) return;

		if (assetProps.type === "material")
		{
			const material = MaterialAsset.getMaterialAssetByGuid(assetProps.nodeId);
			material?.handleSubmit('name', trimmedName);
		}
		else if (assetProps.type === AssetTypes.UITemplate)
		{
			store.dispatch(StoreActions.ui.renameSlide({ id: assetProps.nodeId, name: trimmedName }));
		}
		else if (assetProps.type === AssetTypes.VisageTemplate)
		{
			const currentTemplateData: IVisageTemplate = JsonCopy(store.getState().visageModule.uiTemplateData[assetProps.nodeId]);
			currentTemplateData.name = trimmedName;

			store.dispatch(StoreActions.visage.setUITemplateData({ templateId: assetProps.nodeId, data: currentTemplateData }));
		}
	}

	static renameAssetOnServer(assetProps: IAssetProps, newName: string)
	{
		const trimmedName = newName?.trim();
		if (!trimmedName) return;

		const urlencoded = new URLSearchParams();
		urlencoded.append("asset", assetProps.nodeId);
		urlencoded.append("srcroot", assetProps.tag);
		urlencoded.append("name", trimmedName);

		axios.post(config.SERVER_ADDRESS + config.RENAME_ASSET_PATH, urlencoded)
			.then((response) =>
			{
				if (response.status === 200)
				{
					refreshAssetsFromServer();
				}
			})
			.catch(function (error)
			{
				console.log(error);
			});
	}

	static refreshTimeout;
	static refresh(delay = 300)
	{
		// just make sure we don't get excessive refreshes by things like for loops etc
		clearTimeout(AssetsLibraryUtilities.refreshTimeout);
		AssetsLibraryUtilities.refreshTimeout = setTimeout(() =>
		{
			store.dispatch(StoreActions.assets.update());
			ScenarioHealthUtils.CheckAssetLibrary();
		}, delay);
	}

	static moveAssetOnServer(source, destination)
	{
		const sessionInfo = store.getState().userModule.sessionInfo!;
		const urlencoded = new URLSearchParams();
		urlencoded.append("asset", source.nodeId);
		urlencoded.append("folder", destination.nodeId);
		urlencoded.append("ownerId", destination.tag === "User" ? sessionInfo.cmsUserGqlId : sessionInfo.companies[0].uuid);

		axios.post(config.SERVER_ADDRESS + config.MOVE_ASSET_PATH, urlencoded)
			.then((response) =>
			{
				if (response.status === 200)
				{
					refreshAssetsFromServer();
				}
			})
			.catch(function (error)
			{
				console.log(error);
			});
	}

	static canAssetBeMoved(sourceNodeProps, targetNodeProps)
	{
		if (sourceNodeProps.type === AssetTypes.RevisionProcessed ||
				sourceNodeProps.type === AssetTypes.Material ||
				sourceNodeProps.type === AssetTypes.UITemplate ||
				sourceNodeProps.protected === true
		)
		{
			return false;
		}

		if (!sourceNodeProps.folder && sourceNodeProps.tag === targetNodeProps.nodeId)
		{
			return false;
		}

		if (sourceNodeProps.tag !== targetNodeProps.tag)
		{
			return true;
		}

		if (targetNodeProps.type === 'root')
		{
			return true; //root element
		}

		const library = store.getState().assetModule.library!;
		let assetCollection;
		switch(targetNodeProps.tag)
		{
			case AssetSubTrees.Organization:
				assetCollection = library.organization;
				break;
			case AssetSubTrees.User:
				assetCollection = library.user;
				break;
			default:
				console.warn("assets can only be moved to user or organization trees");
				break;
		}

		let folderId = targetNodeProps.folder;
		//iterate to find the folder path containing the target folder
		while (folderId)
		{
			if (folderId === AssetSubTrees.User || folderId === AssetSubTrees.Organization)
			{
				break;
			}
			if (folderId === sourceNodeProps.nodeId)
			{
				return false;
			}
			folderId = assetCollection[folderId].folder;
		}

		return true;
	}

	static getIconForAssetType(type: AssetTypes, scale = 0.75, style = IconFillStyles.default)
	{
		switch(type)
		{
			case AssetTypes.Material:
				return <MaterialAssetIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.Revision:
				return <ModelFileIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.Model:
				return <ModelFileIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.Folder:
				return <FolderIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.Image:
				return <ImageFileIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.Video:
				return <VideoFileIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.Audio:
				return <AudioFileIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.UITemplate:
				return <UiTemplateIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.ModelProcessed:
				return <ModelFileIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.RevisionProcessed:
				return <ModelFileIcon scale={scale} fillStyle={style}/>;
			case AssetTypes.Document:
				return <UiTemplateIcon scale={scale} fillStyle={style}/>;
			default:
				return <HelpIcon scale={scale} />
		}
	}

	/**
	 * @static
	 * @param {{
	 * autoScroll: boolean,
	 * autoExpand: boolean,
	 * autoRename: boolean,
	 * once: boolean,
	 * }} settings an object that defines modifiers for the asset list view rendering, and a flag to whether or not these changes should be applied only once and then cleared
	 *
	 * available parameters (not all parameters need to be supplied; missing counts as false):
	 * - *autoScroll:* will automatically scroll to the selected asset
	 * - *autoExpand:* will expand list items until the selected asset is visible
	 * - *autoRename:* will begin the rename function on the selected asset
	 * - *once:* defaults to 'true', where the asset library will clear these settings after it has rendered once
	 * @memberof AssetsLibraryUtilities
	 */
	static setRenderSettings(settings)
	{
		store.dispatch(StoreActions.assets.setRenderSettings({once: true, ...settings}));
	}

	/**
	 * @static
	 * @param {[string]} assetIds array of asset ids to select (note: currently only the first one will count as the asset library doesn't support multi-selection)
	 * @param {{}}
	 * object containing override settings to the asset library list rendering. Not all members need to be specified, as defaults are provided for each
	 * internally calls AssetLibraryUtilities.setRenderSettings to apply these modifiers
	 *
	 * available parameters (not all parameters need to be supplied; missing counts as false):
	 * - *autoScroll:* will automatically scroll to the selected asset
	 * - *autoExpand:* will expand list items until the selected asset is visible
	 * - *autoRename:* will begin the rename function on the selected asset
	 * - *once:* defaults to 'true', where the asset library will clear these settings after it has rendered once
	 * @memberof AssetsLibraryUtilities
	 */
	static selectAssets(assetIds, renderParams = {})
	{
		store.dispatch(StoreActions.assets.setSelectedAssets([...assetIds]));
		AssetsLibraryUtilities.setRenderSettings(renderParams);
	}

	static deleteAsset(assetProps: any)
	{
		// local assets (materials)
		if (assetProps.nodeId === "materials")
		{
			store.dispatch(StoreActions.material.clearMaterials());
		}
		else if (assetProps.type === AssetTypes.Material)
		{
			MaterialAsset.deleteMaterial(assetProps.nodeId);
		}
		else if (assetProps.nodeId === "slides")
		{
			//deleteAllSlides
			store.dispatch(StoreActions.ui.deleteAllSlides());
		}
		else if (assetProps.type === AssetTypes.UITemplate)
		{
			//delete a slide
			if (listTemplateTypes().includes(assetProps.nodeId))
			{
				MessagePrompter.showError({
					code: 403,
					title: "Cannot delete template",
					content: "Default templates cannot be deleted.",
				});
			}
			else
			{
				new DeleteTemplate(assetProps.nodeId);
			}
		}
		else if (assetProps.type === AssetTypes.VisageTemplate)
		{
			// WC-4729: do not allow deletion of visage templates that are in use
			if(!AssetsManager.isAssetUsedInProject(assetProps.nodeId))
			{
				new DeleteUITemplateAction(assetProps.nodeId);
			}
		}
		else
		{
			AssetsLibraryUtilities.removeAssetOnServer(assetProps.nodeId, assetProps.tag);
		}
	}
}
